import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const OrganizationDetails = ({ organization }) => {
  const [activeSection, setActiveSection] = useState('about');

  const sections = [
    { id: 'about', label: 'About', icon: 'Info' },
    { id: 'leadership', label: 'Leadership', icon: 'Users' },
    { id: 'programs', label: 'Programs', icon: 'Target' },
    { id: 'contact', label: 'Contact', icon: 'Mail' }
  ];

  const renderAboutSection = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-card-foreground mb-3">Our Mission</h3>
        <p className="text-muted-foreground leading-relaxed">
          {organization?.mission}
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-card-foreground mb-3">Our Vision</h3>
        <p className="text-muted-foreground leading-relaxed">
          {organization?.vision}
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-card-foreground mb-3">History</h3>
        <p className="text-muted-foreground leading-relaxed">
          {organization?.history}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-semibold text-card-foreground mb-2">Founded</h4>
          <p className="text-muted-foreground">{organization?.founded}</p>
        </div>
        <div>
          <h4 className="font-semibold text-card-foreground mb-2">Tax ID</h4>
          <p className="text-muted-foreground font-mono">{organization?.taxId}</p>
        </div>
        <div>
          <h4 className="font-semibold text-card-foreground mb-2">Legal Status</h4>
          <p className="text-muted-foreground">{organization?.legalStatus}</p>
        </div>
        <div>
          <h4 className="font-semibold text-card-foreground mb-2">Registration</h4>
          <p className="text-muted-foreground">{organization?.registration}</p>
        </div>
      </div>
    </div>
  );

  const renderLeadershipSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {organization?.leadership?.map((leader) => (
          <div key={leader?.id} className="flex items-start space-x-4 p-4 border border-border rounded-lg">
            <div className="w-16 h-16 rounded-full overflow-hidden">
              <Image
                src={leader?.photo}
                alt={leader?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-card-foreground">{leader?.name}</h4>
              <p className="text-sm text-primary font-medium">{leader?.position}</p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                {leader?.bio}
              </p>
              {leader?.credentials && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {leader?.credentials?.map((credential, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-muted text-xs text-muted-foreground rounded"
                    >
                      {credential}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderProgramsSection = () => (
    <div className="space-y-6">
      {organization?.programs?.map((program) => (
        <div key={program?.id} className="border border-border rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-card-foreground">{program?.name}</h4>
              <p className="text-muted-foreground mt-1">{program?.description}</p>
            </div>
            <div className="text-right ml-4">
              <div className="text-2xl font-bold text-primary">{program?.beneficiaries}</div>
              <div className="text-xs text-muted-foreground">Beneficiaries</div>
            </div>
          </div>

          {program?.image && (
            <div className="mb-4">
              <Image
                src={program?.image}
                alt={program?.name}
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <h5 className="font-medium text-card-foreground mb-1">Budget</h5>
              <p className="text-muted-foreground">${program?.budget?.toLocaleString()}</p>
            </div>
            <div>
              <h5 className="font-medium text-card-foreground mb-1">Duration</h5>
              <p className="text-muted-foreground">{program?.duration}</p>
            </div>
            <div>
              <h5 className="font-medium text-card-foreground mb-1">Status</h5>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                program?.status === 'Active' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
              }`}>
                {program?.status}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderContactSection = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Icon name="MapPin" size={20} className="text-primary mt-1" />
            <div>
              <h4 className="font-semibold text-card-foreground">Address</h4>
              <p className="text-muted-foreground whitespace-pre-line">
                {organization?.contact?.address}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Icon name="Phone" size={20} className="text-primary" />
            <div>
              <h4 className="font-semibold text-card-foreground">Phone</h4>
              <p className="text-muted-foreground">{organization?.contact?.phone}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Icon name="Mail" size={20} className="text-primary" />
            <div>
              <h4 className="font-semibold text-card-foreground">Email</h4>
              <p className="text-muted-foreground">{organization?.contact?.email}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Icon name="Globe" size={20} className="text-primary" />
            <div>
              <h4 className="font-semibold text-card-foreground">Website</h4>
              <a 
                href={organization?.contact?.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {organization?.contact?.website}
              </a>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-card-foreground mb-4">Office Hours</h4>
          <div className="space-y-2">
            {organization?.contact?.hours?.map((schedule, index) => (
              <div key={index} className="flex justify-between">
                <span className="text-muted-foreground">{schedule?.days}</span>
                <span className="text-card-foreground">{schedule?.time}</span>
              </div>
            ))}
          </div>

          {organization?.contact?.socialMedia && (
            <div className="mt-6">
              <h4 className="font-semibold text-card-foreground mb-3">Follow Us</h4>
              <div className="flex space-x-3">
                {organization?.contact?.socialMedia?.map((social) => (
                  <Button
                    key={social?.platform}
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(social?.url, '_blank')}
                    iconName={social?.icon}
                  >
                    {social?.platform}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="h-64 rounded-lg overflow-hidden border border-border">
        <iframe
          width="100%"
          height="100%"
          loading="lazy"
          title={organization?.name}
          referrerPolicy="no-referrer-when-downgrade"
          src={`https://www.google.com/maps?q=${organization?.contact?.coordinates?.lat},${organization?.contact?.coordinates?.lng}&z=14&output=embed`}
        />
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Building" size={24} className="text-primary" />
        <h2 className="text-xl font-semibold text-card-foreground">Organization Details</h2>
      </div>
      {/* Section Tabs */}
      <div className="flex space-x-1 mb-6 bg-muted rounded-lg p-1 overflow-x-auto">
        {sections?.map((section) => (
          <button
            key={section?.id}
            onClick={() => setActiveSection(section?.id)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-micro whitespace-nowrap ${
              activeSection === section?.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={section?.icon} size={16} />
            <span>{section?.label}</span>
          </button>
        ))}
      </div>
      {/* Section Content */}
      <div>
        {activeSection === 'about' && renderAboutSection()}
        {activeSection === 'leadership' && renderLeadershipSection()}
        {activeSection === 'programs' && renderProgramsSection()}
        {activeSection === 'contact' && renderContactSection()}
      </div>
    </div>
  );
};

export default OrganizationDetails;